import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, CheckCircle } from 'lucide-react';
import InvestmentProfileFields from './access-request/InvestmentProfileFields';
import FinancialProfileFields from './access-request/FinancialProfileFields';
import PersonalInfoFields from './access-request/PersonalInfoFields';
import { useAccessRequestForm } from './hooks/useAccessRequestForm';

const AccessRequestSuccess = ({ onClose }) => (
  <div className="text-center py-8 space-y-4">
    <div className="flex justify-center">
      <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center">
        <CheckCircle className="w-10 h-10 text-emerald-600" />
      </div>
    </div>
    <DialogHeader>
      <DialogTitle className="text-2xl text-center">Request Submitted!</DialogTitle>
      <DialogDescription className="text-center text-base">
        Thank you for your interest in MyInvestFolio.
      </DialogDescription>
    </DialogHeader>
    <Button onClick={onClose} className="bg-emerald-600 hover:bg-emerald-700 w-full">Close</Button>
  </div>
);

const AccessRequestModal = ({ open, onClose, introductionData, prefillToken, prefillFromUser }) => {
  const {
    formData, isSubmitting, isSuccess,
    handleInputChange, handleCheckboxChange,
    handleSubmit, handleClose, briefingOrigin,
  } = useAccessRequestForm(introductionData, onClose, prefillToken, prefillFromUser);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        {isSuccess ? (
          <AccessRequestSuccess onClose={handleClose} />
        ) : (
          <div className="space-y-4">
            <DialogHeader>
              <DialogTitle className="text-2xl">Request an Invitation to MyInvestFolio</DialogTitle>
              <DialogDescription>
                Tell us a bit about yourself. We&apos;ll review your request and, if it&apos;s a good fit, send your invitation by email.
              </DialogDescription>
            </DialogHeader>

            {introductionData && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800"><strong>You've been privately selected</strong></p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <PersonalInfoFields formData={formData} onInputChange={handleInputChange} isSubmitting={isSubmitting} />
              <InvestmentProfileFields formData={formData} onInputChange={handleInputChange} onCheckboxChange={handleCheckboxChange} isSubmitting={isSubmitting} />
              <FinancialProfileFields formData={formData} onInputChange={handleInputChange} isSubmitting={isSubmitting} />

              <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-800">
                <p className="font-medium mb-1">Why do we need this?</p>
                <p className="text-blue-700">
                  MyInvestFolio is a high-trust platform for discerning investors. We carefully review each request to ensure the best experience for our community.
                </p>
                <p className="text-blue-600 mt-2 text-xs">Your information is encrypted and never shared.</p>
              </div>

              <div className="flex gap-3 pt-4 pb-2">
                <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                  {isSubmitting ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Submitting...</>) : 'Submit Request'}
                </Button>
              </div>
            </form>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AccessRequestModal;
